<template>
  <div>
    <div class="modal-header">
      <div class="create-forum-post-header">Create a post</div>
      <button type="button" class="close btn-close" @click="closeModal">
        <img src="../../assets/cross-alt.svg" alt="close" />
      </button>
    </div>

    <div class="modal-body" style="padding-top: 4px">
      <form enctype="multipart/form-data" @submit.prevent="submit">
        <input
          type="text"
          id="title-inp"
          class="form-group form-control"
          placeholder="Post title"
          v-model="title"
        />
        <textarea
          type="text"
          id="body-inp"
          class="form-group form-control"
          style="resize: none"
          rows="5"
          placeholder="Message"
          v-model="postBody"
        />

        <div v-if="postType !== 'post'">
          <TagSelector
            @tag-selection-updated="tagsUpdated"
            :availableTags="getAvailableTags"
          />
          <div style="font-size: 12px; padding-bottom: 20px">
            Use tags to help people find your post by searching for common terms
          </div>

          <!-- Upload / attach -->
          <div v-if="postType !== 'post'">
            <div style="padding-bottom: 8px">Attach...</div>
            <div class="d-flex">
              <div class="attach-panel-button" @click="showAttachPage">
                <div class="d-flex justify-content-center">
                  <img
                    src="../../assets/add-alt.svg"
                    alt="scenario"
                    class="mr-2"
                  />
                  {{ postType === "scenario" ? "Scenario" : "Media" }} from
                  Library
                </div>
              </div>

              <div v-if="postType !== 'media'" class="attach-panel-button" @click="showUploadPage">
                <div class="d-flex justify-content-center">
                  <img src="../../assets/upload.svg" alt="new" class="mr-2" />
                  Upload New
                </div>
              </div>
            </div>
          </div>

          <!-- Files to Attach -->
          <div>
            <div
              class="m-2"
              v-for="scenario in scenariosToAttach"
              v-bind:key="scenario.id"
            >
              <ScenarioMiniTile :scenario="scenario" />
            </div>
            <div v-for="mediaItem in mediaToAttach" v-bind:key="mediaItem.id">
              <MediaMiniTile :media="mediaItem" />
            </div>
          </div>

          <!-- Files to Upload -->
          <template v-if="scenariosToUpload.length">
            <div class="create-forum-post-header mt-3">Scenarios to Upload</div>
            <div v-for="uploadScenario in scenariosToUpload">
              {{ uploadScenario.name }}
            </div>
          </template>
          <template v-if="mediaToUpload.length">
            <div class="create-forum-post-header mt-3">Media to Upload</div>
            <div v-for="uploadMedia in mediaToUpload">
              {{ uploadMedia.name }}
            </div>
          </template>

          <div
            v-if="uploadErrors.length"
            v-for="err in uploadErrors"
            :key="err.filename"
            class="error-msg"
          >
            {{ err.filename }}: {{ err.message }}
          </div>
        </div>

        <!-- Submit -->
        <div class="d-flex">
          <div class="float-right ml-auto">
            <button
              type="submit"
              :disabled="title.trim().length <= 0 || isSubmitting"
              :class="[
                'btn',
                'mt-2',
                title.trim().length <= 0 ? 'btn-secondary' : 'btn-primary',
              ]"
            >
              {{ isSubmitting ? "..." : "Post" }}
            </button>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import ScenService from "@/services/scenarios.service";
import TagSelector from "@/components/TagSelector";
import ScenarioMiniTile from "@/components/ScenarioMiniTile";
import MediaMiniTile from "@/components/MediaMiniTile";
import { PAGES } from "@/store/createForumPostStore";
import { mapState } from "vuex";

export default {
  name: "PostDetails",
  components: {
    TagSelector,
    ScenarioMiniTile,
    MediaMiniTile,
  },
  computed: {
    ...mapState("createForumPostStore", {
      currentPage: (state) => state.currentPage,
      postType: (state) => state.postType,
      uploadErrors: (state) => state.uploadErrors,
      filesToUpload: (state) => state.filesToUpload,
      scenariosToAttach: (state) => state.scenariosToAttach,
      mediaToAttach: (state) => state.mediaToAttach,
      scenariosToUpload: (state) => state.scenariosToUpload,
      mediaToUpload: (state) => state.mediaToUpload,
      isSubmitting: (state) => state.isSubmitting,
      title: (state) => state.title,
      postBody: (state) => state.postBody,
      selectedTags: (state) => state.selectedTags,
    }),
    title: {
      get() {return this.$store.state.createForumPostStore.title},
      set(val) {this.$store.commit("createForumPostStore/setTitle", val)}
    },
    postBody: {
      get() {return this.$store.state.createForumPostStore.postBody},
      set(val) {this.$store.commit("createForumPostStore/setPostBody", val)}
    }
  },
  props: ["isGroup", "groupId"],
  methods: {
    submit() {
      if (this.isSubmitting) return;

      this.$store.commit("createForumPostStore/setIsSubmitting", true);
      this.$store.commit("createForumPostStore/setUploadErrors", []);

      let formData = new FormData();
      formData.append("title", this.title);
      formData.append("text", this.postBody);
      this.scenariosToAttach.forEach((scen) =>
        formData.append("scenarios", scen.id)
      );
      this.mediaToAttach.forEach((media) => formData.append("media", media.id));
      this.scenariosToUpload.forEach((file) =>
        formData.append("scenariosToUpload", file)
      );
      this.mediaToUpload.forEach((file) =>
        formData.append("mediaToUpload", file)
      );
      this.selectedTags.forEach((tag) => formData.append("tags", tag));

      this.$store
        .dispatch("createForumPostStore/createPost", {
          isGroup: this.isGroup,
          groupId: this.groupId,
          formData: formData,
        })
        .finally(() => {
          const parsed = JSON.parse(JSON.stringify(this.uploadErrors));
          
          if (parsed.length > 0){
            return this.resetFormWithErrors(parsed);
          }

          this.resetForm();
        });
    },
    resetFormWithErrors(errors) {
      const parsed = errors.map(e => `${e.filename}: ${e.message}`).join("\n");
      this.$emit("post-failed", parsed);
    },
    resetForm() {
      this.$emit("post-created");
    },
    tagsUpdated(tags) {
      this.$store.commit("createForumPostStore/setSelectedTags", tags);
    },
    getAvailableTags() {
      return ScenService.scenarioTags();
    },
    closeModal() {
      this.$store.dispatch("createForumPostStore/closeModal");
    },
    showAttachPage() {
      this.$store.commit("createForumPostStore/setCurrentPage", PAGES.Attach);
    },
    showUploadPage() {
      this.$store.commit("createForumPostStore/setCurrentPage", PAGES.Upload);
    },
  },
};
</script>

<style scoped>
.create-forum-post-header {
  color: black;
  font-weight: bold;
}

.btn-close {
  border: none;
  font-size: 20px;
  padding: 20px;
  cursor: pointer;
  font-weight: bold;
  color: #4aae9b;
  background: transparent;
}

.attach-panel-button {
  user-select: none;
  padding: 0.4rem;
  margin-right: 0.6rem;
  background: var(--isim-primary-light);
  color: var(--isim-secondary);
  font-size: small;
  font-weight: 600;
  cursor: pointer;

  transition: background 250ms ease-in-out, transform 150ms ease;
  border-radius: 8px;
}

.modal-header {
  border: none;
  padding-top: 24px;
}

.btn-close {
  font-size: 16px;
}

.error-msg {
  color: var(--isim-danger);
}
</style>
